import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search as SearchIcon, X, Plus } from "lucide-react";
import { RememberedSearchInput, SearchCameraButton, SearchGo } from "@/components/search/engine";
import { useVehicleCategories } from "@/lib/vehicleLists";

const OPS = { number: [["gte", "≥"], ["lte", "≤"], ["eq", "="]], text: [["contains", "contains"]], enum: [["eq", "is"]], bool: [["eq", "is"]] };

// Just the dynamic spec-filter rows (category-driven). Reused inside TruckFilterRail (drawer)
// AND dropped into the Vehicles list, which already has its own search + category + operational
// filters — so both surfaces share the identical spec-filter logic.
export function TruckSpecFilters({ engine, pfx = "tf-" }) {
  const { sp, setF } = engine;
  const category = sp.get("category") || "";
  const specFilters = (() => { try { return JSON.parse(sp.get("truck_filters") || "[]"); } catch { return []; } })();
  const [fields, setFields] = useState([]);

  useEffect(() => {
    if (!category || category === "all") { setFields([]); return; }
    api.get("/vehicle-specs/fields", { params: { category } })
      .then(({ data }) => setFields((data.items || []).filter((f) => f.searchable)))
      .catch(() => setFields([]));
  }, [category]);

  const writeFilters = (arr) => setF({ truck_filters: arr.length ? JSON.stringify(arr) : "", page: "" });
  const fieldByKey = (k) => fields.find((f) => f.key === k) || fields[0];
  const addFilter = () => { if (fields[0]) writeFilters([...specFilters, { key: fields[0].key, op: OPS[fields[0].type][0][0], value: "" }]); };
  const setFilter = (i, patch) => writeFilters(specFilters.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const rmFilter = (i) => writeFilters(specFilters.filter((_, j) => j !== i));

  if (!category || category === "all") {
    // Not a dead end and not a silent one. Spec filters are category-driven because the fields
    // ARE (a boat has no rear axle) — so say which control unlocks them instead of rendering
    // nothing and letting someone conclude the feature doesn't exist.
    return (
      <p data-testid={`${pfx}spec-filters-need-category`} className="text-[11px] text-slate-500">
        Pick a <b className="text-slate-700">category</b> above and its spec fields appear here —
        front axle rating, transmission make, GVWR and the rest. The fields differ by vehicle type,
        which is why this waits for one.
      </p>
    );
  }
  return (
    <div className="space-y-2" data-testid={`${pfx}spec-filters`}>
      {specFilters.map((flt, i) => {
        const f = fieldByKey(flt.key);
        if (!f) return null;
        return (
          <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`${pfx}filter-${i}`}>
            <Select value={flt.key} onValueChange={(v) => { const nf = fieldByKey(v); setFilter(i, { key: v, op: OPS[nf.type][0][0], value: "" }); }}>
              <SelectTrigger className="rounded-lg h-9 w-44"><SelectValue /></SelectTrigger>
              <SelectContent>{fields.map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={flt.op} onValueChange={(v) => setFilter(i, { op: v })}>
              <SelectTrigger className="rounded-lg h-9 w-28"><SelectValue /></SelectTrigger>
              <SelectContent>{(OPS[f.type] || OPS.text).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            {f.type === "enum" ? (
              <Select value={String(flt.value)} onValueChange={(v) => setFilter(i, { value: v })}>
                <SelectTrigger data-testid={`${pfx}filter-val-${i}`} className="rounded-lg h-9 w-40"><SelectValue placeholder="value" /></SelectTrigger>
                <SelectContent>{(f.enum_options || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            ) : f.type === "bool" ? (
              <Select value={String(flt.value)} onValueChange={(v) => setFilter(i, { value: v === "true" })}>
                <SelectTrigger data-testid={`${pfx}filter-val-${i}`} className="rounded-lg h-9 w-28"><SelectValue placeholder="yes/no" /></SelectTrigger>
                <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
              </Select>
            ) : (
              <Input data-testid={`${pfx}filter-val-${i}`} value={flt.value} onChange={(e) => setFilter(i, { value: e.target.value })}
                type={f.type === "number" ? "number" : "text"} placeholder={f.unit || "value"} className="rounded-lg h-9 w-40" />
            )}
            <button onClick={() => rmFilter(i)} className="p-1 text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button>
          </div>
        );
      })}
      <Button data-testid={`${pfx}add-filter`} size="sm" variant="outline" onClick={addFilter} className="rounded-lg h-9"><Plus className="w-4 h-4 mr-1" />Add spec filter</Button>
    </div>
  );
}

// SHARED truck filter UI — free-text search + category + dynamic spec filters — driven by a
// useSearchEngine({scope:"trucks"}) instance. Used by BOTH the Vehicles list and the Search-Trucks
// drawer so the two surfaces run the IDENTICAL engine + filter logic (no parallel implementations).
export function TruckFilterRail({ engine, pfx = "tf-" }) {
  const { sp, setF } = engine;
  const categories = useVehicleCategories();
  const activeCats = categories.filter((c) => !c.archived && c.code !== "uncategorized");
  const category = sp.get("category") || "";

  return (
    <div className="space-y-3" data-testid={`${pfx}filter-rail`}>
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-1.5 flex-1 min-w-[12rem]">
          <div className="relative flex-1">
            <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <RememberedSearchInput testid={`${pfx}truck-search-input`} value={sp.get("q") || ""}
              placeholder="Keyword: stock #, VIN, make, model, spec, serial…"
              onChange={(v) => setF({ q: v, page: "" })} delay={300}
              className="rounded-lg h-10 pl-9 bg-white" />
          </div>
          <SearchGo testid={`${pfx}truck-search-input`} onGo={(v) => setF({ q: v, page: "" })} size="h-10" />
          <SearchCameraButton testid={`${pfx}truck-search-input`} onScan={(v) => setF({ q: v, page: "" })} />
        </div>
        <Select value={category || "__none"} onValueChange={(v) => setF({ category: v === "__none" ? "" : v, truck_filters: "", page: "" })}>
          <SelectTrigger data-testid={`${pfx}category`} className="rounded-lg h-10 w-52 bg-white"><SelectValue placeholder="Category (for spec filters)…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">All categories</SelectItem>
            {activeCats.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`${pfx}cat-${c.code}`}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <TruckSpecFilters engine={engine} pfx={pfx} />
    </div>
  );
}
